import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth, formatError } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  MessageSquare, Car, Wrench, Zap, FileText, Tag, Calendar,
  Star, Search, ChevronRight, Plus, Users, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import AttachmentPicker from '../components/AttachmentPicker';
import AdSlot from '../components/AdSlot';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import RichTextEditor from '../components/RichTextEditor';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_ICONS = {
  genel: MessageSquare,
  l6e: Car,
  l7e: Car,
  'arac-incelemeleri': Star,
  'teknik-bakim': Wrench,
  'sarj-batarya': Zap,
  mevzuat: FileText,
  'ikinci-el': Tag,
  'etkinlikler-forum': Calendar,
};

const CATEGORY_STYLES = {
  genel: { bg: 'bg-slate-50', icon: 'bg-slate-100 text-slate-600', border: 'border-slate-200', badge: 'bg-slate-100 text-slate-600' },
  l6e: { bg: 'bg-emerald-50', icon: 'bg-emerald-100 text-emerald-700', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700' },
  l7e: { bg: 'bg-cyan-50', icon: 'bg-cyan-100 text-cyan-700', border: 'border-cyan-200', badge: 'bg-cyan-100 text-cyan-700' },
  'arac-incelemeleri': { bg: 'bg-violet-50', icon: 'bg-violet-100 text-violet-700', border: 'border-violet-200', badge: 'bg-violet-100 text-violet-700' },
  'teknik-bakim': { bg: 'bg-orange-50', icon: 'bg-orange-100 text-orange-700', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700' },
  'sarj-batarya': { bg: 'bg-yellow-50', icon: 'bg-yellow-100 text-yellow-700', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-700' },
  mevzuat: { bg: 'bg-blue-50', icon: 'bg-blue-100 text-blue-700', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700' },
  'ikinci-el': { bg: 'bg-rose-50', icon: 'bg-rose-100 text-rose-700', border: 'border-rose-200', badge: 'bg-rose-100 text-rose-700' },
  'etkinlikler-forum': { bg: 'bg-purple-50', icon: 'bg-purple-100 text-purple-700', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700' },
};

// Admin panelinden seçilen renk (cat.color) için stiller — slug tabanlı fallback'ten önce önceliklidir
const COLOR_STYLES = {
  emerald: { bg: 'bg-emerald-50', icon: 'bg-emerald-100 text-emerald-700', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700' },
  sky: { bg: 'bg-sky-50', icon: 'bg-sky-100 text-sky-700', border: 'border-sky-200', badge: 'bg-sky-100 text-sky-700' },
  amber: { bg: 'bg-amber-50', icon: 'bg-amber-100 text-amber-700', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700' },
  rose: { bg: 'bg-rose-50', icon: 'bg-rose-100 text-rose-700', border: 'border-rose-200', badge: 'bg-rose-100 text-rose-700' },
  violet: { bg: 'bg-violet-50', icon: 'bg-violet-100 text-violet-700', border: 'border-violet-200', badge: 'bg-violet-100 text-violet-700' },
  slate: { bg: 'bg-slate-50', icon: 'bg-slate-100 text-slate-600', border: 'border-slate-200', badge: 'bg-slate-100 text-slate-600' },
  // bonus (üretilmiş veri uyumluluğu)
  cyan: { bg: 'bg-cyan-50', icon: 'bg-cyan-100 text-cyan-700', border: 'border-cyan-200', badge: 'bg-cyan-100 text-cyan-700' },
  orange: { bg: 'bg-orange-50', icon: 'bg-orange-100 text-orange-700', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700' },
  yellow: { bg: 'bg-yellow-50', icon: 'bg-yellow-100 text-yellow-700', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-700' },
  blue: { bg: 'bg-blue-50', icon: 'bg-blue-100 text-blue-700', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700' },
  purple: { bg: 'bg-purple-50', icon: 'bg-purple-100 text-purple-700', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700' },
};

const styleFor = (cat) => (cat && COLOR_STYLES[cat.color]) || CATEGORY_STYLES[cat?.slug] || CATEGORY_STYLES.genel;

export default function ForumPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [recentTopics, setRecentTopics] = useState([]);
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [showNewTopic, setShowNewTopic] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newAttachments, setNewAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/forum/categories`).then(r => setCategories(r.data.categories || []));
    axios.get(`${API}/forum/topics?sort=recent&limit=10`).then(r => setRecentTopics(r.data.topics || []));
  }, []);

  // Navbar'dan ya da link'ten gelen ?q=... parametresi ile otomatik arama tetikle
  useEffect(() => {
    const q = searchParams.get('q');
    if (q && q.trim()) {
      setSearch(q);
      setSearching(true);
      setSearchPerformed(true);
      axios.get(`${API}/forum/topics?search=${encodeURIComponent(q)}&limit=20`)
        .then(r => setSearchResults(r.data.topics || []))
        .finally(() => setSearching(false));
    }
  }, [searchParams]);

  const openNewTopic = () => {
    if (!user) {
      toast.error('Konu açmak için giriş yapmalısın');
      navigate('/giris');
      return;
    }
    setShowNewTopic(true);
  };

  const handleNewTopic = async (e) => {
    e.preventDefault();
    if (!newCategory) {
      toast.error('Kategori seç');
      return;
    }
    if (!newTitle.trim() || !newContent.trim()) {
      toast.error('Başlık ve içerik zorunludur');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/forum/topics`, {
        category_slug: newCategory,
        title: newTitle,
        content: newContent,
        attachments: newAttachments,
      }, { withCredentials: true });
      toast.success('Konu açıldı!');
      setShowNewTopic(false);
      setNewCategory('');
      setNewTitle('');
      setNewContent('');
      setNewAttachments([]);
      navigate(`/forum/konu/${r.data.topic.slug || r.data.topic._id}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    setSearching(true);
    setSearchPerformed(true);
    setSearchParams({ q: search.trim() });
    try {
      const r = await axios.get(`${API}/forum/topics?search=${encodeURIComponent(search)}&limit=20`);
      setSearchResults(r.data.topics || []);
    } finally {
      setSearching(false);
    }
  };

  function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  }

  const CSTYLES = CATEGORY_STYLES;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO
        title="Forum"
        description="MiniOto forumunda L6e ve L7e sahipleriyle tartış: menzil, şarj, bakım, mevzuat ve etkinlikler. Yeni konu aç veya mevcut konulara katıl."
      />
      <Breadcrumbs items={[{ name: 'Forum' }]} />
      <AdSlot placement="forum_top" className="mb-6" />
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Forum</h1>
          <p className="text-slate-500 mt-1">L6e & L7e topluluğu ile bilgi paylaş</p>
        </div>
        {user && (
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={openNewTopic}
            data-testid="forum-new-topic-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Yeni Konu
          </Button>
        )}
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Konularda ara..."
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              if (!e.target.value) {
                setSearchResults([]);
                setSearchPerformed(false);
                if (searchParams.get('q')) setSearchParams({});
              }
            }}
            className="pl-10"
            data-testid="forum-search-input"
          />
        </div>
        <Button type="submit" disabled={searching} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="forum-search-btn">
          {searching ? 'Aranıyor...' : 'Ara'}
        </Button>
      </form>

      {/* Search results */}
      {searchPerformed && !searching && searchResults.length === 0 && (
        <div className="mb-8 bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800" data-testid="forum-search-empty">
          "<span className="font-semibold">{search}</span>" için konu bulunamadı. Farklı anahtar kelimeler deneyin.
        </div>
      )}
      {searchResults.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-bold text-slate-700 mb-3">Arama Sonuçları ({searchResults.length})</h2>
          <div className="space-y-2">
            {searchResults.map(t => (
              <Link key={t._id} to={`/forum/konu/${t.slug || t._id}`} data-testid={`search-result-${t._id}`}
                className="block bg-white rounded-xl border border-slate-200 p-4 hover:border-emerald-300 transition-colors">
                <div className="font-semibold text-slate-800">{t.title}</div>
                <div className="text-xs text-slate-400 mt-1">{t.category_name} · {t.author_display_name}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Categories grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {categories.map(cat => {
          const Icon = CATEGORY_ICONS[cat.slug] || MessageSquare;
          const styles = styleFor(cat);
          return (
            <Link
              key={cat.slug}
              to={`/forum/kategori/${cat.slug}`}
              data-testid={`forum-cat-${cat.slug}`}
              className={`group rounded-2xl border ${styles.border} ${styles.bg} p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${styles.icon}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-slate-800 group-hover:text-emerald-700 transition-colors">{cat.name}</h3>
                    {(cat.slug === 'l6e' || cat.slug === 'l7e') && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${styles.badge}`}>
                        {cat.slug === 'l6e' ? '45 km/s' : '90 km/s'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-1 line-clamp-1">{cat.description}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{cat.topic_count || 0} konu</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 transition-colors shrink-0" />
              </div>
            </Link>
          );
        })}
      </div>

      {/* Recent topics */}
      <div>
        <h2 className="text-xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Son Eklenen Konular</h2>
        {recentTopics.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
            <MessageSquare className="w-12 h-12 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-500">Henüz konu yok. İlk konuyu siz açın!</p>
            {user && (
              <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={openNewTopic} data-testid="forum-new-topic-btn-empty">
                Yeni Konu Aç
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-50">
            {recentTopics.map(topic => (
              <Link
                key={topic._id}
                to={`/forum/konu/${topic.slug || topic._id}`}
                data-testid={`forum-topic-${topic._id}`}
                className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CSTYLES[topic.category_slug]?.badge || 'bg-slate-100 text-slate-600'}`}>
                      {topic.category_name}
                    </span>
                    {topic.is_pinned && <span className="text-xs bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded-full">Sabit</span>}
                    {topic.is_locked && <span className="text-xs bg-red-50 text-red-700 px-1.5 py-0.5 rounded-full">Kilitli</span>}
                  </div>
                  <div className="font-semibold text-slate-800 truncate">{topic.title}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{topic.author_display_name} · {formatDate(topic.created_at)}</div>
                </div>
                <div className="hidden sm:flex items-center gap-4 text-xs text-slate-400 shrink-0">
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{topic.views || 0}</span>
                  <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{topic.reply_count || 0}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      {/* New Topic Dialog */}
      <Dialog open={showNewTopic} onOpenChange={setShowNewTopic}>
        <DialogContent className="sm:max-w-[560px]" data-testid="new-topic-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Yeni Konu Aç</DialogTitle>
            <DialogDescription>Kategori seç, başlık ve içerik yaz, topluluğa paylaş.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleNewTopic} className="space-y-4 mt-2">
            <div>
              <Label htmlFor="new-topic-category">Kategori</Label>
              <Select value={newCategory} onValueChange={setNewCategory}>
                <SelectTrigger id="new-topic-category" className="mt-1" data-testid="new-topic-category">
                  <SelectValue placeholder="Kategori seç..." />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(c => (
                    <SelectItem key={c.slug} value={c.slug} data-testid={`new-topic-cat-opt-${c.slug}`}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="new-topic-title">Başlık</Label>
              <Input
                id="new-topic-title"
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                placeholder="Konu başlığı..."
                className="mt-1"
                data-testid="new-topic-title"
              />
            </div>
            <div>
              <Label htmlFor="new-topic-content">İçerik</Label>
              <div className="mt-1" data-testid="new-topic-content">
                <RichTextEditor
                  value={newContent}
                  onChange={setNewContent}
                  placeholder="Düşüncelerini paylaş..."
                  minHeight={200}
                />
              </div>
            </div>
            <div>
              <Label>Ekler (opsiyonel)</Label>
              <div className="mt-1">
                <AttachmentPicker attachments={newAttachments} setAttachments={setNewAttachments} testIdPrefix="forum-new-attach" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowNewTopic(false)} data-testid="new-topic-cancel">İptal</Button>
              <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-topic-submit">
                {submitting ? 'Gönderiliyor...' : 'Konu Aç'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
